import dayjs from 'dayjs'
import { ElMessage } from 'element-plus'
import { defineStore } from 'pinia'
import {
  connect,
  ConnectOptions,
  createLocalAudioTrack,
  createLocalTracks,
  CreateLocalTracksOptions,
  createLocalVideoTrack,
  LocalAudioTrack,
  LocalDataTrack,
  LocalParticipant,
  LocalTrack,
  LocalVideoTrack,
  RemoteAudioTrack,
  RemoteAudioTrackPublication,
  RemoteParticipant,
  RemoteTrack,
  RemoteTrackPublication,
  RemoteVideoTrack,
  RemoteVideoTrackPublication,
  Room,
  Track,
  TwilioError,
} from 'twilio-video'
import { ComputedRef } from 'vue'

import {
  trackKindDeviceMap,
  TROUBLE_SHOOT_MESSAGES,
} from '@shared/constants/video-call'
import EventBus, { VideoCallEvents } from '@shared/event-bus'
import i18n from '@shared/i18n'
import { Namespaces } from '@shared/models/enums/namespaces'
import {
  ParticipantIdentitiesKeys,
  ParticipantRole,
} from '@shared/models/interfaces/conversations'
import {
  DataMessage,
  DataMessageTypes,
  MediaPayloadType,
} from '@shared/models/interfaces/quick-messages'
import {
  IAnyNetworkStats,
  PC_RtcSessionUpdateDTO,
  VC_RtcSessionUpdateDTO,
} from '@shared/models/proto/dto/rtc-session'
import { Nullable } from '@shared/models/types/primary'
import { resourcePath } from '@shared/plugins/api'
import { ApiTransport } from '@shared/plugins/api-transport'
import { useTimeDiff } from '@shared/utils/composables/time-diff'
import { defaultParticipantNameMap } from '@shared/utils/constants/video-call-configs'
import { MediaErrorType } from '@shared/utils/helpers/error-guards/media-errors'
import { isMobileDevice } from '@shared/utils/helpers/is-mobile-device'
import {
  lsGet,
  LsGlobalKeys,
  lsHas,
  lsRemove,
  lsSave,
} from '@shared/utils/helpers/local-storage'
import { handleUserFullName } from '@shared/utils/helpers/user-name'
import {
  detachTracks,
  getParticipantRoleFromType,
  getUserMediaTrack,
  initTracksAndDevices,
} from '@shared/utils/helpers/video-call'
import { CompatibilityFailure } from '@shared/utils/models/video-call-compatibility'
import {
  FacingMode,
  VideoCallConfigNamespace,
  VideoCallSession,
} from '@shared/utils/models/video-call-config'

import { RtcStatus } from '@protos/rtc_status_pb'
import { UserType } from '@protos/user_type_pb'

import { IConversationPatient } from '@pc/models/proto/base/conversations'
import { useSessionStore } from '@pc/store/session'
import { IConversation } from '@vc/models/proto/base/conversations'

import { useGeneralStore } from './general'

const extractParticipantId = (identity: string) => identity.split(':')[0]

export const getParticipantId = (identity: string) =>
  extractParticipantId(identity)

export const getParticipantRole = (identity: string): ParticipantRole =>
  identity.split(':')[1] as ParticipantRole

type TrackKind = Track.Kind

interface ParticipantIdentity {
  identity: string
  id: string
  presence: boolean
  audio: boolean
  video: boolean
}

const { DifferenceTracker } = useTimeDiff()
const differenceTracker = new DifferenceTracker()
interface State {
  isScreenSharing: boolean
  isVideoChatMode: boolean
  isVoiceChatMode: boolean
  isPictureInPictureMode: boolean
  isAudioEnabled: boolean
  isVideoEnabled: boolean
  mediaError: Nullable<MediaErrorType>
  compatibilityError: Nullable<CompatibilityFailure>
  isLoading: boolean
  twilioError: Nullable<TwilioError>
  facingMode: FacingMode

  startedAt: Nullable<dayjs.Dayjs>
  callDuration: ComputedRef<string>
  token: Nullable<string>
  roomId: Nullable<string>
  invitationLink: string
  conversation: Nullable<IConversation | IConversationPatient>

  devices: {
    cameras: MediaDeviceInfo[]
    microphones: MediaDeviceInfo[]
  }
  selectedDeviceId: {
    camera: Nullable<string>
    microphone: Nullable<string>
  }
  netQualityLevel: {
    [k: string]: number
  }
  participantIdentities: {
    [ParticipantIdentitiesKeys.FIRST]?: ParticipantIdentity
    [ParticipantIdentitiesKeys.SECOND]?: ParticipantIdentity
  }
  troubleShootingMessageWindow: {
    isShown: boolean
    message: string
    participantName: string
    senderId?: number
  }
  localNetworkStatistics: IAnyNetworkStats

  pictureInPictureIdentity: Nullable<string>

  networkStatsTime: number

  aloneTextOverride: string
}

export let room: Nullable<Room> = null

let networkStatsInterval = 0
let updateRTCSessionInterval = 0
let screenTrack: Nullable<LocalVideoTrack> = null

const state = (): State => ({
  // TODO Stop using localStorage CurrentVideoChat values blindly
  //   These should be used to request RtcSession information from back end BEFORE attempting to connect
  //   It is possible the user has come back after the chat is over
  //   We should not interrupt user experience to connect to a video chat that they can't even connect to

  // TODO Use the RtcSession information in State somewhere so we can use startedAt and other properties
  isVideoChatMode: false,
  isScreenSharing: false,
  isVoiceChatMode: false,
  isPictureInPictureMode: false,
  isAudioEnabled: true,
  isVideoEnabled: true,
  mediaError: null,
  isLoading: false,
  twilioError: null,
  compatibilityError: null,
  facingMode: 'user',

  startedAt: null,
  callDuration: differenceTracker.durationString,
  token: null,
  roomId: null,
  conversation:
    JSON.parse(lsGet(LsGlobalKeys.CurrentVideoChatConversation)) || null,
  invitationLink: '',

  devices: {
    cameras: [],
    microphones: [],
  },
  selectedDeviceId: {
    camera: null,
    microphone: null,
  },
  localNetworkStatistics: {
    key: '',
    values: [],
  },
  troubleShootingMessageWindow: {
    isShown: false,
    message: '',
    participantName: '',
  },

  netQualityLevel: {},
  participantIdentities: {},

  pictureInPictureIdentity: null,

  networkStatsTime: 0,

  aloneTextOverride: '',
})

export const useVideoCallConfigsStore = defineStore('video-call-configs', {
  state,

  actions: {
    async initializeTracks() {
      this.setCachedVideoChatData()

      let localTracks: LocalTrack[] = []

      try {
        const { tracks: trackList, mediaError } = await getUserMediaTrack()

        this.mediaError = mediaError

        if (mediaError || !trackList) {
          // eslint-disable-next-line
          console.error('Error : MediaError >>', mediaError)
          return
        }

        const { cameraDevices, microphoneDevices, tracks } =
          await initTracksAndDevices(
            trackList,
            this.isAudioEnabled,
            this.isVideoEnabled
          )

        localTracks = tracks

        const { cameras, microphones } = this.devices

        if (!cameras.length) {
          this.devices.cameras = cameraDevices

          if (!this.selectedDeviceId.camera) {
            this.selectedDeviceId.camera = cameraDevices[0].deviceId
          }
        }

        if (!microphones.length) {
          this.devices.microphones = microphoneDevices

          if (!this.selectedDeviceId.microphone) {
            this.selectedDeviceId.microphone = microphoneDevices[0].deviceId
          }
        }

        room = await this.connectToRoom(localTracks)

        if (!this.startedAt) {
          this.startedAt = dayjs()
        }

        // eslint-disable-next-line
        console.log('connectToRoom room :>> ', room)

        differenceTracker.track(
          () => this.startedAt,
          () => this.isVideoChatMode
        )

        return localTracks
      } catch (e) {
        // eslint-disable-next-line
        console.error('Error : Tracks >>', e)
        this.closeVideoConversation()
        localTracks.forEach((track) => {
          if (track.kind !== 'data') {
            track.stop()
          }
        })

        throw e
      }
    },

    async connectToRoom(
      tracks: (LocalVideoTrack | LocalAudioTrack | LocalDataTrack)[]
    ): Promise<Nullable<Room>> {
      if (!this.token) {
        throw new Error('Cannot connect to room. Missing Token')
      }
      if (!this.roomId) {
        throw new Error('Cannot connect to room. Missing roomId')
      }

      if (isMobileDevice()) {
        this.cacheVideoConversation()
      }

      const connectOptions: ConnectOptions = {
        logLevel: 'error',
        name: this.roomId,
        networkQuality: { local: 3, remote: 3 },
        maxAudioBitrate: 16000,
        preferredVideoCodecs: [{ codec: 'VP8', simulcast: true }],
        tracks,
        audio: this.isAudioEnabled,
        video: this.isVideoEnabled,
      }
      return await connect(this.token, connectOptions)
    },

    applyRoomEventListeners(props: { namespace: VideoCallConfigNamespace }) {
      if (!room) {
        return
      }

      networkStatsInterval = window.setInterval(() => {
        this.networkStatsTime++
      }, 1000)

      updateRTCSessionInterval = window.setInterval(() => {
        this.updateRTCData({ namespace: props.namespace })
      }, 60000)

      const { localParticipant } = room
      this.initializeLocalParticipant(localParticipant, props.namespace)

      room.on('disconnected', this.onRoomDisconnected)
      room.on('participantConnected', this.initializeParticipant)
      room.on('participantDisconnected', this.onRemoveParticipant)
    },

    onRoomDisconnected(room: Room, error?: TwilioError) {
      const participants = [
        ...room.participants.values(),
        room.localParticipant,
      ]
      participants.forEach((participant) => {
        this.removeParticipantTracks(participant)
      })

      if (error) {
        this.twilioError = error
      }

      window.clearInterval(updateRTCSessionInterval!)

      this.resetStore()
    },

    initializeParticipants() {
      this.refreshLocalDataTrack()

      if (!room) {
        return
      }

      return room.participants.forEach(this.initializeParticipant)
    },

    initializeLocalParticipant(
      localParticipant: LocalParticipant,
      namespace: VideoCallConfigNamespace
    ) {
      this.updatedNetworkStats(
        'local',
        localParticipant.networkQualityLevel ?? 0
      )
      localParticipant.on(
        'networkQualityLevelChanged',
        (qualityLevel: number) => {
          this.updatedNetworkStats('local', qualityLevel)

          this.localNetworkStatistics.key = getParticipantId(
            localParticipant.identity
          ).toString()

          this.localNetworkStatistics.values.push({
            key: this.networkStatsTime.toString(),
            value: qualityLevel,
          })

          this.updateRTCData({ namespace })
        }
      )

      localParticipant.on('trackPublished', (publication) => {
        ;(publication.track as unknown as LocalParticipant).on('muted', () =>
          this.sendDataTrackMessage({
            type: DataMessageTypes.UnintentionalMute,
            payload: { isMuted: true },
          })
        )
        ;(publication.track as unknown as LocalParticipant).on('unmuted', () =>
          this.sendDataTrackMessage({
            type: DataMessageTypes.UnintentionalMute,
            payload: { isMuted: false },
          })
        )
      })
    },

    getParticipantIdentitiesKeys(
      participant: RemoteParticipant
    ): ParticipantIdentitiesKeys {
      const selfIdentity = getParticipantRole(
        room?.localParticipant.identity ?? ''
      )
      const participantRole = getParticipantRole(participant.identity)

      // User:            [Patient/Visitor, Guest]
      // Patient/Visitor: [User, Guest]
      // Guest:           [Patient/Visitor, User]

      const isPatientOrVisitor = (role: ParticipantRole) =>
        [ParticipantRole.PATIENT, ParticipantRole.VISITOR].includes(role)

      if (isPatientOrVisitor(selfIdentity)) {
        return participantRole === ParticipantRole.USER
          ? ParticipantIdentitiesKeys.FIRST
          : ParticipantIdentitiesKeys.SECOND
      }

      return isPatientOrVisitor(participantRole)
        ? ParticipantIdentitiesKeys.FIRST
        : ParticipantIdentitiesKeys.SECOND
    },

    initializeParticipant(participant: RemoteParticipant) {
      const patientId = getParticipantId(participant.identity)
      const participantKey = this.getParticipantIdentitiesKeys(participant)

      this.participantIdentities[participantKey] = {
        identity: participant.identity,
        id: patientId,
        presence: true,
        audio: true,
        video: true,
      }

      participant.tracks.forEach((publication: RemoteTrackPublication) => {
        this.trackPublished(participant, publication)
      })

      // Set up remote video notifications for the VideoTracks that will be
      // published later.
      participant.on(
        'trackPublished',
        (publication: RemoteTrackPublication) => {
          this.trackPublished(participant, publication)
        }
      )

      participant.on('networkQualityLevelChanged', (qualityLevel: number) => {
        this.updatedNetworkStats(participant.identity, qualityLevel)
      })

      const { first } = this.participantIdentities

      if (this.isPictureInPictureMode && first) {
        this.startPictureInPictureMode(first.id)
      }
    },

    trackPublished(
      participant: RemoteParticipant,
      publication: RemoteTrackPublication
    ) {
      // No special handling needed for data tracks here
      if (publication.track && publication.kind !== 'data') {
        const mediaPublication = publication as
          | RemoteAudioTrackPublication
          | RemoteVideoTrackPublication

        // First let's detach anything exisitng. Just in case we get duplicates.
        const tracksMap =
          publication.track.kind === 'audio'
            ? participant.audioTracks
            : participant.videoTracks

        const tracksToDetach = [...tracksMap.values()]
          .map((pub) => pub.track)
          .filter(Boolean) as RemoteTrack[]

        detachTracks(tracksToDetach, true)

        EventBus.emit(VideoCallEvents.AttachTracks, {
          tracks: [mediaPublication.track],
          isLocal: false,
          participantRole: getParticipantRole(participant.identity),
          isSecond:
            this.participantIdentities[ParticipantIdentitiesKeys.SECOND]
              ?.identity === participant.identity,
        })
      }
      // Once the TrackPublication is subscribed to, attach the Track to the DOM.
      publication.on('subscribed', (track) =>
        this.trackSubscribed(track, participant)
      )
      // Once the TrackPublication is unsubscribed from, detach the Track from the DOM.
      publication.on('unsubscribed', (track) => {
        detachTracks([track], true)
      })
    },

    trackSubscribed(track: RemoteTrack, participant: RemoteParticipant) {
      // Adding listeners to data tracks
      if (track.kind === 'data') {
        return track.on('message', (message: string) =>
          this.handleDataTrackMessage(message)
        )
      }

      this.remoteTrackToggled(track, participant)

      track.on('enabled', (enabledTrack) =>
        this.remoteTrackToggled(enabledTrack, participant)
      )
      track.on('disabled', (disabledTrack) =>
        this.remoteTrackToggled(disabledTrack, participant)
      )

      EventBus.emit(VideoCallEvents.AttachTracks, {
        tracks: [track],
        isLocal: false,
        participantRole: getParticipantRole(participant.identity),
        isSecond:
          this.participantIdentities[ParticipantIdentitiesKeys.SECOND]
            ?.identity === participant.identity,
      })
    },
    remoteTrackToggled(
      track: RemoteAudioTrack | RemoteVideoTrack,
      participant: RemoteParticipant
    ) {
      const key = this.getParticipantIdentitiesKeys(participant)

      const participantIdentity = this.participantIdentities[key]

      if (participantIdentity) {
        participantIdentity[track.kind] = track.isEnabled
      }
    },

    handleDataTrackMessage(jsonMessage: string) {
      const message = JSON.parse(jsonMessage) as DataMessage

      const participant = this.conversation?.participants?.find(
        (p) =>
          p.chatUserId.toString() === getParticipantId(message.identity ?? '')
      )

      const role = getParticipantRole(message.identity ?? '')

      const defaultName = defaultParticipantNameMap[role]

      const participantName = handleUserFullName(
        defaultName ?? i18n.global.t('unknown'),
        participant?.firstName,
        participant?.lastName
      )

      switch (message.type) {
        case DataMessageTypes.RefreshLocalTracks: {
          const sessionStore = useSessionStore()
          const generalStore = useGeneralStore()

          const { message: troubleShootingMessage, recipientIdentity } =
            message.payload

          const recipientId = getParticipantId(recipientIdentity)

          const isMe =
            sessionStore.isMe(recipientId) ||
            generalStore.chatUserIsMe(Number(recipientId))

          if (!isMe) {
            return
          }

          this.setTroubleShootingMessageWindow(
            true,
            TROUBLE_SHOOT_MESSAGES[troubleShootingMessage],
            participantName
          )
          break
        }
        case DataMessageTypes.HangUp: {
          if (Object.keys(this.participantIdentities).length > 1) {
            break
          }
          this.disconnectFromRoom()
          this.closeVideoConversation()
          break
        }
        case DataMessageTypes.PresenceNotification: {
          const { message: troubleShootingMessage } = message.payload
          this.setTroubleShootingMessageWindow(
            true,
            troubleShootingMessage,
            participantName
          )
          break
        }

        case DataMessageTypes.UnintentionalMute: {
          const remoteParticipant = Array.from(
            room?.participants.values() ?? []
          ).find(({ identity }) => identity === message.identity)

          if (!remoteParticipant) {
            break
          }

          const participantKey =
            this.getParticipantIdentitiesKeys(remoteParticipant)

          const participantIsMuted =
            this.participantIdentities[participantKey]?.audio

          if (message.payload.isMuted && !participantIsMuted) {
            this.setTroubleShootingMessageWindow(
              true,
              i18n.global.t('chat.video.dataMessages.unintentionalMute', {
                name: participantName,
              }),
              participantName
            )
          } else {
            this.setTroubleShootingMessageWindow(false)
          }
          break
        }
        default: {
          // inexisting data message type
        }
      }
    },

    onVideoChatCancel(conversationId: number, chatUserId: number) {
      if (conversationId !== this.conversation?.id) {
        return
      }

      const participant = this.conversation?.participants?.find(
        (p) => p.chatUserId === chatUserId
      )

      const participantRole = getParticipantRoleFromType(
        participant?.type ?? UserType.VIDEO_CALL_GUEST
      )

      const defaultName = defaultParticipantNameMap[participantRole]

      const participantName = handleUserFullName(
        defaultName ?? i18n.global.t('unknown'),
        participant?.firstName,
        participant?.lastName
      )

      this.aloneTextOverride = i18n.global.t(
        'chat.video.troubleshooting.remoteParticipantTroubleshooting',
        { name: participantName }
      )
    },

    setTroubleShootingMessageWindow(
      isShown: boolean,
      message?: string,
      participantName?: string,
      senderId?: number
    ) {
      this.troubleShootingMessageWindow = {
        message: message || '',
        participantName: participantName || '',
        isShown,
        senderId,
      }
    },

    removeParticipant(participant: LocalParticipant | RemoteParticipant) {
      this.removeParticipantTracks(participant)
      if (!room) {
        return
      }
      room.participants.forEach((p) => {
        this.refreshParticipantTracks(p)
        const pId = getParticipantId(participant.identity)
        if (
          this.participantIdentities[ParticipantIdentitiesKeys.FIRST]?.id ===
          pId
        ) {
          delete this.participantIdentities[ParticipantIdentitiesKeys.FIRST]
        } else {
          delete this.participantIdentities[ParticipantIdentitiesKeys.SECOND]
        }
        delete this.netQualityLevel[pId]
      })
      // the logic for reloading the page if the video call interrupted without clicking "hang on"
      if (room?.participants?.size) {
        return
      }

      setTimeout(() => {
        if (!room || room?.participants?.size) {
          return
        }

        this.setTroubleShootingMessageWindow(false)
        this.disconnectFromRoom()
        this.closeVideoConversation()
      }, 20000)

      setTimeout(() => {
        if (!room || room?.participants?.size) {
          return
        }
        this.setTroubleShootingMessageWindow(
          true,
          i18n.global.t(
            'chat.video.troubleshooting.messages.participantHasIssues'
          )
        )
      }, 4000)
    },

    onRemoveParticipant(participant: LocalParticipant | RemoteParticipant) {
      this.removeParticipant(participant)
    },

    removeParticipantTracks(participant: LocalParticipant | RemoteParticipant) {
      type Tracks = (LocalTrack | RemoteTrack)[]
      // Filter and map tracks from publications that have them
      const tracks = [...participant.tracks.values()].reduce<Tracks>(
        (acc, { track }) => (track && acc.push(track), acc),
        []
      )

      detachTracks(tracks, true)
    },

    disconnectFromRoom() {
      // eslint-disable-next-line
      console.log('disconnectFromRoom :>> ')
      if (room) {
        room.disconnect()
        this.removeRoomData()
      }

      this.isVideoChatMode = false
      window.clearInterval(networkStatsInterval)
      this.resetStore()
    },

    refreshParticipantTracks(
      participant: RemoteParticipant,
      types: TrackKind[] = ['audio', 'video']
    ) {
      // Filter and map tracks from publications that have them
      let tracks = [...participant.tracks.values()].reduce(
        (acc: RemoteTrack[], { track }) => (track && acc.push(track), acc),
        []
      )
      if (types) {
        tracks = tracks.filter(({ kind }) => types.includes(kind))
      }

      detachTracks(tracks, true)

      if (room?.state === 'connected') {
        ;[...participant.tracks.values()].map((publication) =>
          this.trackPublished(participant, publication)
        )
      }
    },

    async refreshLocalMediaTracks(
      types: MediaPayloadType[] = ['audio', 'video'],
      trackParams?: CreateLocalTracksOptions
    ) {
      const refreshPromises = []

      if (types.includes('audio')) {
        if (this.isAudioEnabled) {
          refreshPromises.push(
            this.restartOrRecreateLocalMediaTracks('audio', trackParams)
          )
        } else {
          this.toggleLocalAudioTrack(false)
        }
      }

      if (types.includes('video')) {
        if (this.isVideoEnabled) {
          refreshPromises.push(
            this.restartOrRecreateLocalMediaTracks('video', trackParams)
          )
        } else {
          this.toggleLocalVideoTrack(false)
        }
      }

      return await Promise.all(refreshPromises)
    },

    async refreshLocalDataTrack() {
      room?.localParticipant.dataTracks.forEach((track) => track.unpublish())

      const dataTrack = new LocalDataTrack()

      await room?.localParticipant.publishTrack(dataTrack)
    },

    async restartOrRecreateLocalMediaTracks(
      type: TrackKind,
      trackParams?: CreateLocalTracksOptions
    ) {
      const publications = [
        ...(room?.localParticipant.tracks.values() ?? []),
      ].filter(({ kind }) => kind === type)
      const tracks = publications.map(({ track }) => track)
      // If we only have one track, just restart it
      room?.localParticipant.unpublishTracks(tracks)

      detachTracks(tracks, true)

      let newLocalTracks

      const deviceType = trackKindDeviceMap[type]

      if (trackParams) {
        newLocalTracks = await createLocalTracks(trackParams)
      } else if (this.selectedDeviceId[deviceType!]) {
        trackParams = {
          [type]: {
            deviceId: {
              exact: this.selectedDeviceId[deviceType!],
            },
          },
        }
        newLocalTracks = await createLocalTracks(trackParams)
      } else {
        const createTrack =
          type === 'audio' ? createLocalAudioTrack : createLocalVideoTrack

        newLocalTracks = [await createTrack()]

        const mediaToEnable =
          type === 'audio' ? 'isAudioEnabled' : 'isVideoEnabled'

        this[mediaToEnable] = true

        this.selectedDeviceId[type === 'audio' ? 'microphone' : 'camera'] =
          newLocalTracks[0].mediaStreamTrack.getCapabilities().deviceId!
      }

      await room?.localParticipant.publishTracks(newLocalTracks)
      EventBus.emit(VideoCallEvents.AttachTracks, {
        tracks: newLocalTracks as (LocalVideoTrack | LocalAudioTrack)[],
        isLocal: true,
      })

      return newLocalTracks
    },
    reattachLocalTrack() {
      if (!room) {
        return
      }

      const localParticipant = room.localParticipant

      EventBus.emit(VideoCallEvents.AttachTracks, {
        tracks: [...localParticipant.videoTracks.values()].map(
          (pub) => pub.track
        ),
        isLocal: true,
      })
    },

    reattachAllTracks() {
      if (!room) {
        return
      }

      this.reattachLocalTrack()

      const remoteParticipants = [...room.participants.values()]

      remoteParticipants.forEach((participant) => {
        EventBus.emit(VideoCallEvents.AttachTracks, {
          tracks: [...participant.videoTracks.values()].map((pub) => pub.track),
          participantRole: getParticipantRole(participant.identity),
          isLocal: false,
        })
      })
    },
    async switchCamera() {
      const newFacingMode = this.facingMode === 'user' ? 'environment' : 'user'

      this.facingMode = newFacingMode

      const trackParams: CreateLocalTracksOptions = {
        video: {
          facingMode: {
            exact: newFacingMode,
          },
        },
      }

      await this.restartOrRecreateLocalMediaTracks('video', trackParams)
    },

    async toggleLocalAudioTrack(enabled?: boolean) {
      if (!room) {
        return
      }

      this.isAudioEnabled = enabled ?? !this.isAudioEnabled

      if (room.localParticipant.audioTracks.size) {
        // If we have tracks already, just enable/disable

        room.localParticipant.audioTracks.forEach((publication) => {
          publication.track?.enable(this.isAudioEnabled)
        })
      } else if (this.isAudioEnabled) {
        // If we don't have tracks but we need to enable some, make new ones

        const trackParams: CreateLocalTracksOptions = {
          audio: {
            deviceId: { exact: this.selectedDeviceId.microphone ?? '' },
          },
        }

        await this.refreshLocalMediaTracks(['audio'], trackParams)
      }

      this.sendDataTrackMessage({
        type: DataMessageTypes.MediaNotification,
        payload: {
          audio: this.isAudioEnabled,
        },
      })

      return this.isAudioEnabled
    },

    async toggleLocalVideoTrack(enabled?: boolean) {
      if (!room) {
        return false
      }

      this.isVideoEnabled = enabled ?? !this.isVideoEnabled

      if (this.isVideoEnabled) {
        const trackParams: CreateLocalTracksOptions = {
          video: { deviceId: { exact: this.selectedDeviceId.camera ?? '' } },
        }

        await this.refreshLocalMediaTracks(['video'], trackParams)
      } else {
        const videoPublications = room.localParticipant.videoTracks
        videoPublications.forEach((publication) => {
          if (publication.track) {
            detachTracks([publication.track], true)
            // Instant set timeout because we need the unpublish event to be at the bottom of the JS stack
            // Otherwise, unpublish fires before the disable event and prevents it from firing
            // Blame JS event loop
            setTimeout(() => publication.unpublish(), 0)
          }
        })
      }

      this.sendDataTrackMessage({
        type: DataMessageTypes.MediaNotification,
        payload: {
          video: this.isVideoEnabled,
        },
      })

      return this.isVideoEnabled
    },

    sendDataTrackMessage(message: DataMessage) {
      if (!room) {
        return
      }
      const { dataTracks, identity } = room.localParticipant
      // Attach identity to log who sent what message
      message.identity = identity

      const jsonMessage = JSON.stringify(message)

      // We get a Map, but we only ever need exactly one data track
      // If there is more than one data track, something went wrong!
      // There is also a small chance that there is no data track! This happens sometimes while disconnecting.
      const tracks = [...dataTracks.values()]
      tracks.length && tracks[0].track.send(jsonMessage)
    },

    async setSelectedDeviceId(type: 'camera' | 'microphone', id: string) {
      this.selectedDeviceId[type] = id
      const inputType = type === 'camera' ? 'video' : 'audio'

      // Only refresh tracks if the input is enabled
      if (
        (inputType === 'video' && this.isVideoEnabled) ||
        (inputType === 'audio' && this.isAudioEnabled)
      ) {
        const trackParams: CreateLocalTracksOptions = {
          [inputType]: { deviceId: { exact: id } },
        }
        await this.restartOrRecreateLocalMediaTracks(inputType, trackParams)
      }
    },

    updatedNetworkStats(id: string, qualityLevel: number) {
      const identity = id === 'local' ? id : getParticipantId(id)
      this.netQualityLevel[identity] = qualityLevel
    },

    startPictureInPictureMode(identity: Nullable<string | boolean>) {
      if (this.isVoiceChatMode) {
        this.isPictureInPictureMode = Boolean(identity)

        EventBus.emit(VideoCallEvents.AttachTracks, {
          tracks: [],
          isLocal: false,
        })

        return
      }

      if (!room) {
        return
      }

      this.isPictureInPictureMode = Boolean(identity)

      if (!this.isPictureInPictureMode) {
        this.pictureInPictureIdentity = null
        return
      }

      const allParticipants = [
        ...room.participants.values(),
        room.localParticipant,
      ]

      allParticipants.forEach((p) => {
        const roomParticipantId = getParticipantId(p.identity)

        if (roomParticipantId === identity) {
          EventBus.emit(VideoCallEvents.AttachTracks, {
            tracks: [...p.videoTracks.values()].map((pub) => pub.track),
            participantRole: getParticipantRole(p.identity),
            isLocal: false,
          })
        }
      })

      // This needs to be set at the end
      // We use this in PictureInPictureWindow.vue in case Events are emitted before mount
      // This is needed for router navigation
      this.pictureInPictureIdentity = identity as string
    },

    screenShare(setBackgroundCallback?: () => void) {
      if (!room) {
        return
      }

      const getTrackByType = (type: MediaPayloadType) => {
        const localTracks = room?.localParticipant.tracks.values() ?? []
        return [...localTracks]
          .filter(({ kind }) => kind === type)
          .map(({ track }) => track)
      }

      const videoTracks = getTrackByType('video')

      room.localParticipant.unpublishTracks(videoTracks)
      detachTracks(videoTracks, true)

      navigator.mediaDevices
        .getDisplayMedia()
        .then((stream) => {
          screenTrack = new LocalVideoTrack(stream.getTracks()[0])
          room?.localParticipant.publishTrack(screenTrack)
          EventBus.emit(VideoCallEvents.AttachTracks, {
            tracks: [screenTrack],
            isLocal: true,
          })

          this.isScreenSharing = true

          screenTrack.on('stopped', async () => {
            await this.onStopScreenSharing()
            setBackgroundCallback && setBackgroundCallback()
          })
        })
        .catch(async (err) => {
          if (err.name === 'NotAllowedError') {
            // User canceled the screen sharing prompt
          } else {
            // Handle other errors that may occur during screen sharing
            ElMessage.info(i18n.global.t('chat.video.screenSharingError'))
          }
          await this.refreshLocalMediaTracks()
          setBackgroundCallback && setBackgroundCallback()
        })
    },

    async onStopScreenSharing() {
      room?.localParticipant.unpublishTrack(screenTrack!)
      screenTrack?.stop()
      detachTracks([screenTrack!], true)
      this.isScreenSharing = false
      screenTrack = null

      /**
       * @description
       * There are two cases when the screen sharing may be stopped:
       *
       * 1. When the user cancells it on his own via browser UI (clicking on "Do not share anymore")
       *
       * 2. When the user hangs up the video call while screen sharing
       *
       * So here we check whether we should re-create local user
       * media tracks (video/audio) after screen sharing stopped in this 2 cases.
       *
       * If it was stopped via hang up - don't re-create user's local tracks (audio/video)
       * */

      if (this.isVideoChatMode) {
        await this.refreshLocalMediaTracks()
      }
    },

    updateRTCData(props: { namespace: VideoCallConfigNamespace }) {
      const apiTransport = new ApiTransport(resourcePath, props.namespace)

      if (!this.roomId) {
        return
      }

      const dto =
        apiTransport.namespace === Namespaces.PC
          ? PC_RtcSessionUpdateDTO
          : VC_RtcSessionUpdateDTO

      const payload = {
        roomId: this.roomId,
        data: {
          failedReason: '',
          status: RtcStatus.NET_STATS,
          requestedAt: new Date().toISOString(),
          networkStats: [this.localNetworkStatistics],
        },
      }

      return apiTransport.put('/rtc_sessions', new dto(payload), dto)
    },
    closeVideoConversation() {
      this.cleanCachedVideoConversation()
      this.isVideoChatMode = false
      this.conversation = null
    },

    cleanCachedVideoConversation() {
      lsRemove(LsGlobalKeys.CurrentVideoCall)
      lsRemove(LsGlobalKeys.CurrentVideoChatConversation)
    },

    cacheVideoConversation() {
      lsSave(
        LsGlobalKeys.CurrentVideoChatConversation,
        this.conversation || null
      )
      const currentVideoCall: VideoCallSession = {
        roomId: this.roomId || null,
        token: this.token || null,
        startedAt: this.startedAt || null,
        isAudioEnabled: this.isAudioEnabled,
        isVideoEnabled: this.isVideoEnabled,
      }
      lsSave(LsGlobalKeys.CurrentVideoCall, currentVideoCall)
    },

    setCachedVideoChatData() {
      if (!this.token || !this.roomId) {
        if (lsHas(LsGlobalKeys.CurrentVideoCall)) {
          const videoChat: VideoCallSession =
            JSON.parse(lsGet(LsGlobalKeys.CurrentVideoCall)) || {}
          const {
            roomId = null,
            token = null,
            startedAt = null,
            isAudioEnabled,
            isVideoEnabled,
          } = videoChat

          this.startedAt = startedAt
          this.roomId = roomId
          this.token = token
          this.isVideoEnabled = isVideoEnabled
          this.isAudioEnabled = isAudioEnabled
          this.cleanCachedVideoConversation()
        }
      }
    },

    resetStore() {
      // should keep roken, roomId for fetch VideoCallFeedbackForm
      // Also needed to rehydrate after router navigation
      const { token, roomId, participantIdentities } = this

      this.$reset()

      this.token = token
      this.roomId = roomId
      this.participantIdentities = participantIdentities
    },

    removeRoomData() {
      this.roomId = null
      this.participantIdentities = {}
    },
  },

  getters: {
    myNetQualityLevel(state) {
      return state.netQualityLevel.local
    },

    isAbleToSwitchCamera(state) {
      return state.isVideoEnabled && state.devices.cameras.length > 1
    },
  },
})
